import { Link } from "react-router-dom";
import {
    ArrowRight, BadgePercent, Check, CreditCard, Globe, Inbox, Loader2, Megaphone, Package,
    ShoppingBag, Store, Ticket,
} from "lucide-react";
import BackOffice, { useBackOffice } from "@/components/account/BackOffice";
import StatTiles from "@/components/account/StatTiles";
import { Btn, Section } from "@/components/account/Shell";
import { Cell, Sheet, Tag, Tiles, Txt } from "@/components/account/Sheet";
import { useAuth } from "@/context/AuthContext";

const LABELS = {
    pending_payment: "Awaiting payment", paid: "Paid", in_production: "In production",
    ready: "Ready for pickup", completed: "Picked up", cancelled: "Cancelled",
};
const JOB_COLS = [
    { key: "num", label: "Order #", sub: "opens the work ticket", w: "120px" },
    { key: "customer", label: "Customer", w: "minmax(200px,1.5fr)" },
    { key: "lines", label: "Lines", w: "80px" },
    { key: "status", label: "Status", w: "150px" },
    { key: "total", label: "Yours", sub: "$ your lines", w: "100px" },
];

function Checklist({ items }) {
    const done = items.filter((i) => i.done).length;
    return (
        <Section title="Finish your shop" pad={false} testid="dash-checklist" note={`${done} of ${items.length} done`}
            right={
                <span className="block h-1.5 w-32 overflow-hidden bg-[#e5e5e5]">
                    <span className="block h-full bg-[#1a1a1a]" style={{ width: `${(done / items.length) * 100}%` }} />
                </span>
            }>
            {items.map((i) => (
                <Link key={i.key} to={i.to} data-testid={`dash-check-${i.key}`}
                    className={`flex items-center gap-2.5 border-t border-[#e5e5e5] px-2.5 py-1.5 text-sm first:border-t-0 hover:bg-[#fafafa] ${i.done ? "text-[#4a4a4a]" : "text-[#1a1a1a]"}`}>
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center border ${i.done ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#d0d0d0]"}`}>
                        {i.done && <Check size={10} />}
                    </span>
                    <span className={i.done ? "line-through" : "font-semibold"}>{i.label}</span>
                    {!i.done && <ArrowRight size={12} className="ml-auto text-[#4a4a4a]" />}
                </Link>
            ))}
        </Section>
    );
}

const QUICK = [
    { to: "/account/products", icon: Package, label: "Add a product" },
    { to: "/account/orders", icon: Inbox, label: "Work an order" },
    { to: "/account/billboards", icon: Megaphone, label: "Hang a billboard" },
    { to: "/account/coupons", icon: Ticket, label: "Coupons & discounts" },
    { to: "/account/payouts", icon: CreditCard, label: "Get paid" },
];

function SellerDash({ data }) {
    const { store, stats, checklist, recent_orders: recent, plan } = data;
    return (
        <div className="space-y-5" data-testid="seller-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-700">
                        {plan.tier_label}{plan.designers ? " · designers on" : ""}
                        {plan.status !== "active" ? ` · ${plan.status.replace("_", " ")}` : ""}
                    </p>
                    <h1 className="mt-1 font-display text-3xl font-black tracking-tight">{store.name}</h1>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {QUICK.map((q) => (
                        <Link key={q.label} to={q.to} data-testid={`dash-quick-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <Btn><q.icon size={12} /> {q.label}</Btn>
                        </Link>
                    ))}
                </div>
            </div>

            <StatTiles stats={stats} />

            <Checklist items={checklist} />

            <Section title="Latest jobs" pad={false} testid="dash-latest-jobs" note="the newest orders in your shop · click one for its work ticket">
                {recent.length === 0 ? (
                    <div className="p-6 text-center" data-testid="dash-no-orders">
                        <ShoppingBag size={30} className="mx-auto text-zinc-300" />
                        <p className="mt-2.5 text-sm text-zinc-800">No orders in your shop yet.</p>
                        <Link to="/account/billboards" className="mt-4 inline-block">
                            <Btn tone="solid">Hang a billboard and get seen</Btn>
                        </Link>
                    </div>
                ) : (
                    <Sheet cols={JOB_COLS} rows={recent} rowKey={(o) => o.id} testid="dash-jobs-sheet" minWidth={720}
                        rowTestid={(o) => `dash-order-${o.order_number}`}
                        cells={(o) => ({
                            num: <Cell><Link to={`/account/orders/${o.id}/ticket`} className="truncate text-[12px] font-semibold text-[#1a56db] hover:underline">{o.order_number}</Link></Cell>,
                            customer: <Cell><Txt>{o.customer || "—"}</Txt></Cell>,
                            lines: <Cell><Txt mono right>{o.lines}</Txt></Cell>,
                            status: <Cell><Tag status={o.status}>{LABELS[o.status] || o.status}</Tag></Cell>,
                            total: <Cell><Txt bold mono right>${o.my_total.toFixed(2)}</Txt></Cell>,
                        })} />
                )}
            </Section>
        </div>
    );
}

const UPSELLS = [
    {
        key: "space", icon: Store, to: "/spaces", cta: "See the spaces",
        title: "Open a shop on 13thav",
        body: "A Starter shop page is $9 a month. A Storefront is $29 a month and puts your products on every page. A Pro Shop is $79 a month and adds the live designers.",
    },
    {
        key: "own-site", icon: Globe, to: "/spaces", cta: "See the building",
        title: "Or take the whole website",
        body: "Virtual Brick & Mortar is everything on this site as your own private website, with your own web address, for $199 a month or $1,690 a year. The directory and every other shop disappear, and you pay 0% platform fee.",
    },
    {
        key: "gang", icon: BadgePercent, to: "/wholesale", cta: "Get a Wholesale pass",
        title: "Save on everything you buy",
        body: "A Wholesale pass knocks money off every order you place on 13thav — Bronze $5, Silver $50, Gold $150 a month.",
    },
];

function ShopperDash({ data, user }) {
    const b = data.buyer || {};
    return (
        <div className="space-y-5" data-testid="shopper-dashboard">
            <div>
                <h1 className="font-display text-3xl font-black tracking-tight">
                    Hey, {user?.name?.split(" ")[0] || "there"}
                </h1>
                <p className="mt-1 text-zinc-700">{user?.email}</p>
            </div>

            <Tiles testid="shopper-stats" items={[
                ["Orders placed", b.purchases ?? 0, "all time", "shopper-stat-orders-placed"],
                ["Still open", b.open ?? 0, "being made or waiting on you", "shopper-stat-still-open"],
                ["Spent with us", `$${Number(b.spent || 0).toFixed(2)}`, "across every shop", "shopper-stat-spent-with-us"],
            ]} />

            <Section title="Do more on 13thav" pad={false} testid="shopper-upsells" note="one line each · sell here, take the whole site, or save on what you buy">
                {UPSELLS.map((u) => (
                    <div key={u.key} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-[#e5e5e5] px-2.5 py-2.5 first:border-t-0"
                        data-testid={`upsell-${u.key}`}>
                        <u.icon size={16} className="shrink-0 text-[#1a1a1a]" />
                        <div className="min-w-[220px] flex-1">
                            <p className="text-[13px] font-semibold text-[#1a1a1a]">{u.title}</p>
                            <p className="text-[12px] leading-snug text-[#4a4a4a]">{u.body}</p>
                        </div>
                        <Link to={u.to}><Btn>{u.cta} <ArrowRight size={11} /></Btn></Link>
                    </div>
                ))}
            </Section>

            <Link to="/account/purchases" data-testid="shopper-purchases-link" className="inline-block">
                <Btn tone="solid"><ShoppingBag size={12} /> See all my purchases</Btn>
            </Link>
        </div>
    );
}

/** /account — the shop manager if you rent a space, your own shelf if you don't. */
function Dash() {
    const { user } = useAuth();
    const { data } = useBackOffice();

    if (!data) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center" data-testid="dash-loading">
                <Loader2 className="animate-spin text-zinc-400" />
            </div>
        );
    }
    return data.store ? <SellerDash data={data} /> : <ShopperDash data={data} user={user} />;
}

export default function Account() {
    return <BackOffice><Dash /></BackOffice>;
}
